import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import { mergeAdjacentRuns } from '@/lib/text/text-style-engine'

/**
 * Drops the wire-only per-run metrics. `fillTextLineWireMetrics` recomputes all three from the
 * fresh layout on every save, and keeping a stale value on a run that has just been merged back
 * across a former row boundary would leave a measurement in the model describing neither fragment.
 * Stripping is also what makes `unbakeShapeTextParagraphs` idempotent.
 */
export function stripWireRunMetrics(run: TextRun): TextRun {
  const next: TextRun = { ...run }

  delete next.textAdvance
  delete next.textWidth
  delete next.baseline

  return next
}

/**
 * The inverse of `bakeShapeTextWireLines` — collapses a wire paragraph back to larry's *logical*
 * model of exactly one `TextLine` per paragraph.
 *
 * The baked visual form (one `TextLine` per rendered row, carrying `leftMargin`/`baseline`/
 * `descent` and an `isHidden` overset tail) exists so the XML is a faithful transcript for yb360,
 * which paints shape text from that frozen geometry on *its* load. It is **write-only** as far as
 * larry is concerned: larry re-flows from `paragraphsToPlainText` on every render and never reads
 * the baked geometry back. Accepting it into the model anyway left a frame in one of two
 * structurally different states for identical content — N lines just after a reload, 1 line after
 * the next edit — which is a standing trap for every offset-based helper.
 *
 * `mergeAdjacentRuns` is what makes reassembly safe: it compares runs by `extractRunStyle`, which
 * *includes* `fillStyle`/`lineStyle` but *omits* the wire metrics. So the two halves of one source
 * run split at a row boundary collapse back into one, while two genuinely different-coloured
 * yb360 runs stay separate and their authored colours survive the round-trip untouched.
 *
 * `isHidden` is deliberately NOT preserved. A merged line holds both visible and overset text, so
 * no single boolean is correct for it; overflow is recomputed every render from the layout's own
 * `consumedLength` and never read off this flag; and leaving it set would make the helpers that
 * filter hidden lines drop real user text. The overset contract is re-established on the next
 * save, where `bakeShapeTextWireLines` re-derives it from the current layout.
 *
 * Flat text is invariant under the shape convention (`paragraphsToPlainText` joins `textLines`
 * with `''` and counts hidden lines), so every character offset — `buildCharRunMap`,
 * `splitShapeTextParagraphsAtOffset` — is unchanged by this.
 */
export function unbakeShapeTextParagraphs(paragraphs: Paragraph[]): Paragraph[] {
  return paragraphs.map((paragraph) => {
    const lines = paragraph.textLines ?? []

    // Don't synthesize a line where the wire had none: `content.text` key presence, not line
    // count, is the "this is a text frame" signal (WMPCM-8160).
    if (lines.length === 0) return paragraph

    const runs = mergeAdjacentRuns(
      lines.flatMap((line) => line.textRuns ?? []).map(stripWireRunMetrics)
    )

    if (runs.length === 0) return paragraph

    return { ...paragraph, textLines: [{ textRuns: runs }] }
  })
}
